<template>
    <q-card class="datos-presupuesto-card">
        <q-card-section class="presupuesto-header">
            <div class="row items-center no-wrap">
                <div class="col row items-center q-gutter-sm">
                    <q-icon name="description" size="32px" />
                    <div class="text-h5 text-weight-medium">Datos del Presupuesto</div>
                </div>
                <div class="col-auto">
                    <q-chip v-if="presup?.acopio" color="orange" text-color="white" icon="mdi-basket-unfill" size="md">
                        Acopio
                    </q-chip>
                    <q-chip :color="presup?.estado.color" text-color="white" icon="info" size="md">
                        {{ presup?.estado.nombre }}
                    </q-chip>
                </div>
            </div>
        </q-card-section>
        <q-card-section>
            <div class="row text-body1 q-gutter-md q-mb-md">
                <div class="col">
                    <GltnInput outlined v-model="form.user.name" label="Usuario de carga" readonly />
                </div>
                <div class="col">
                    <GltnInput outlined v-model="presup.created_at" label="Fecha de creacion" readonly />
                </div>
                <div class="col">
                    <GltnInput outlined v-model="presup.updated_at" label="Fecha de actualización" readonly />
                </div>
            </div>
            <div class="row text-body1 q-gutter-md q-mb-md">
                <div class="col">
                    <GltnInputFecha outlined v-model="form.fecha_necesidad" label="Fecha de Necesidad" readonly />
                </div>
                <div class="col">
                    <GltnInput label="Vendedor" v-model="form.vendedor" readonly />
                </div>
                <div class="col">
                    <GltnInput outlined v-model="form.tipo_cambio" label="Tipo de Cambio" prefix="U$S" readonly />
                </div>
            </div>
            <div class="row text-body1 q-gutter-md q-mb-md">
                <div class="col">
                    <GltnInput label="Condición de Pago" v-model="form.condicion_pago" readonly />
                </div>
                <div class="col">
                    <GltnSelectUbicacion label="Ubicación" v-model="form.ubicacion_id" emit-value map-options
                        readonly />
                </div>
            </div>
            <div class="row text-body1 q-gutter-md">
                <div class="col">
                    <GltnInput outlined :model-value="presup?.pedido_venta" label="Pedido de Venta" readonly />
                </div>
                <div class="col">
                    <GltnInput outlined :model-value="presup?.factura" label="Factura" readonly />
                </div>
            </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="totales-section">
            <div class="totales-list">
                <div class="total-original-item" v-if="mostrarDatosAcopio">
                    <span class="total-label-original">Total Original</span>
                    <span class="total-valor-original">{{ numberFormat(presup.total_original) }}</span>
                </div>
                <div class="total-item" v-for="(emp, idx) in totalEmp" :key="idx">
                    <q-chip color="primary" text-color="white" size="sm">
                        <q-avatar color="white" text-color="primary" icon="business" />
                        {{ emp.empresa }}
                    </q-chip>
                    <span class="total-valor">{{ numberFormat(emp.total) }}</span>
                </div>
                <q-separator class="q-my-md" />
                <div class="total-final">
                    <span class="total-label">Total Presupuesto</span>
                    <span class="total-valor-final">{{ numberFormat(presup.items_sum_total) }}</span>
                </div>
            </div>
        </q-card-section>
        <q-separator />
        <PresupuestosVinculados :presup="presup" v-if="mostrarDatosAcopio" />
        <q-separator v-if="mostrarDatosAcopio" />
        <q-card-actions class="q-pa-md">
            <div class="row full-width items-center justify-between">
                <div class="row q-gutter-sm">
                    <q-btn outline @click="clonar" color="blue" icon="content_copy" label="Clonar"
                        :loading="loadingClonar" :disable="!checkPermission('presupuesto.clonar')" no-caps />
                    <q-btn v-if="esEstadoAcopio" outline @click="cerrar" color="negative" icon="remove_moderator" label="Cerrar"
                        :disable="!checkPermission('presupuesto.cerrar')" no-caps />

                </div>
                <div>
                    <q-btn unelevated @click="presupuestoCrudDialogOpen = true" color="primary" icon="edit"
                        label="Editar" :disable="!checkPermission('presupuesto.editar')" no-caps>
                        <q-tooltip v-if="!checkPermission('presupuesto.editar')">
                            No tienes permisos para editar
                        </q-tooltip>
                    </q-btn>
                </div>
            </div>
        </q-card-actions>

        <PresupuestoCrudDialog v-model="presupuestoCrudDialogOpen" :presup="presup" @updated="emit('presup-updated')" />

    </q-card>
</template>

<script setup>
import { format, parseISO } from "date-fns";
import { reactive, ref, onMounted, computed } from "vue";
import { router } from "@inertiajs/vue3";
import { useAuthStore } from "@/stores/useAuthStore.js";
import { usePresupuestoHandler } from "@/composables/usePresupuestoHandler.js";
import { useQuasar } from "quasar";
import GltnInput from "@/components/form/GltnInput.vue";
import GltnInputFecha from "@/components/form/GltnInputFecha.vue";
import GltnSelectUbicacion from "@/components/form/GltnSelectUbicacion.vue";
import numberFormat from "@/helpers/numberFormat";
import PresupuestoService from "@/services/PresupuestoService";
import PresupuestoCrudDialog from "./PresupuestoCrudDialog.vue";
import PresupuestosVinculados from "./PresupuestosVinculados.vue";


const { checkPermission } = useAuthStore();
const { exportar, exportLoading } = usePresupuestoHandler();
const $q = useQuasar();
const loadingClonar = ref(false);
const presupuestoCrudDialogOpen = ref(false);
const esEstadoAcopio = computed(() => {
    return presup?.estado?.nombre === "ACOPIO";
});
const emit = defineEmits(["presup-updated"]);
const { presup, totalEmp } = defineProps({
    presup: Object,
    totalEmp: Array,
});

const mostrarDatosAcopio = computed(() => {
    return presup?.estado?.nombre === "ACOPIO" || presup?.estado?.nombre === "ACOPIO CERRADO";
});
const form = reactive({
    condicion_pago: presup?.condicion_pago.E4_XDESCRI || null,
    fecha_necesidad:
        format(parseISO(presup?.fecha_necesidad), "dd-MM-yyyy") || null,
    vendedor: presup?.A3_NOME || null,
    tipo_cambio: presup?.tipo_cambio || null,
    ubicacion_id: presup?.ubicacion_id || null,
    user: {
        name: presup?.user ? presup.user.name : "",
    },
});

const clonar = async () => {
    $q.dialog({
        title: "Clonar Presupuesto",
        message: "<b>¿Está seguro de clonar este presupuesto? </b>",
        html: true,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        loadingClonar.value = true;
        try {
            const { nuevoPresupuesto } =
                await PresupuestoService.presupuestoClonar(presup.id);
            router.visit(`/presupuestos/${nuevoPresupuesto.id}`);
        } catch (error) {
            $q.notify({
                message: "Error al clonar presupuesto: " + error.message,
                icon: "error",
                color: "negative",
            });
            return;
        } finally {
            loadingClonar.value = false;
        }
    });
};
const presupuestoExport = async () => {
    await exportar(presup.id);
};

const eliminar = () => {
    $q.dialog({
        title: "Eliminar Presupuesto",
        message: "<b>¿Está seguro de eliminar este presupuesto?</b>",
        html: true,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        try {
            await PresupuestoService.delete(presup.id);
            $q.notify({
                message: "Presupuesto eliminado correctamente",
                icon: "check_circle",
                color: "positive",
            });
            router.visit("/presupuestos");
        } catch (error) {
            $q.notify({
                message: "Error al eliminar presupuesto: " + error.message,
                icon: "error",
                color: "negative",
            });
        }
    });
};

const cerrar = () => {
    $q.dialog({
        title: "Cerrar Presupuesto",
        message: "<b>¿Está seguro de cerrar este presupuesto?</b><br/> Este procedimiento evitara desgloces a futuro del presupuesto y se generará un nuevo presupuesto en estado DESACOPIO CIERRE con los saldos de los items que hay en el presupuesto.",
        html: true,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        try {
            await PresupuestoService.cierre(presup.id);
            router.visit("/presupuestos");
        } catch (error) {
            console.error(error);
        }
    });
};

</script>

<style lang="scss" scoped>
.datos-presupuesto-card {
    border-radius: 16px;
    overflow: hidden;
}

.presupuesto-header {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;
    padding: 20px 24px;
}

.totales-section {
    background: linear-gradient(to bottom, #fafafa, #f5f5f5);
    padding: 24px;
}

.totales-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.total-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.total-valor {
    font-size: 16px;
    font-weight: 600;
    color: #424242;
}

.total-original-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(245, 124, 0, 0.15);
}

.total-label-original {
    font-size: 16px;
    font-weight: 600;
    color: #e65100;
}

.total-valor-original {
    font-size: 20px;
    font-weight: 700;
    color: #ef6c00;
}

.total-final {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
}

.total-label {
    font-size: 18px;
    font-weight: 600;
    color: #1976d2;
}

.total-valor-final {
    font-size: 24px;
    font-weight: 700;
    color: #1565c0;
}

.action-btn {
    min-width: 120px;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
    }
}

@media (max-width: 768px) {
    .total-final {
        flex-direction: column;
        gap: 8px;
        text-align: center;
    }
}
</style>
